import { useSelection } from '@/app/SelectionContext';
import { useExpenses } from '@/app/hooks';
import { useIsWideScreen } from '@/app/useIsWideScreen';
import { PageHeader } from '@/components/PageHeader';
import { ExportMenu } from '@/components/ExportMenu';
import { Card, SectionTitle } from '@/components/Card';
import { ErrorState } from '@/components/ErrorState';
import { Spinner } from '@/components/LoadingScreen';
import { NoFinancialData } from '@/components/NoFinancialData';
import { ChartCard, ExpenseStackChart } from '@/components/charts';
import { ExtraordinaryExpenses } from '@/features/expenses/ExtraordinaryExpenses';
import { buildPoints } from '@/utils/series';
import {
  NO_DATA,
  formatAmd,
  formatChange,
  formatPercent,
  periodLabel,
  periodShortLabel,
  toNumber,
} from '@/utils/format';
import { font, v } from '@/theme/tokens';
import type { ExpenseDriverRow, ExpenseGroupRow, ExpensesResponse } from '@/types/schemas';

/** Colours are assigned by position, so a group added to the canonical list
 * later still gets one without a change here. */
const GROUP_COLORS = [
  'var(--chart-slate)',
  'var(--chart-blue)',
  'var(--chart-violet)',
  'var(--chart-teal)',
  'var(--chart-amber)',
  'var(--chart-cyan)',
  'var(--chart-lime)',
  'var(--chart-pink)',
];

/** The groups plotted in the structure chart: the five sections that add up
 * to «Все расходы» exactly. PAYROLL is deliberately absent — it lives inside
 * FIXED_EXPENSES, and stacking both would double count. */
const STACKED_GROUP_CODES = [
  'COGS',
  'PRODUCTION_EXPENSES',
  'FIXED_EXPENSES',
  'OPERATIONAL_EXPENSES',
  'OTHER_EXPENSES',
] as const;

export function ExpensesPage() {
  const { selectedUnit, selectedPeriod } = useSelection();
  const query = useExpenses(selectedUnit?.id, selectedPeriod?.id);
  const wide = useIsWideScreen();

  const data = query.data;
  const isRestaurant = selectedUnit?.type !== 'PRODUCTION_KITCHEN';
  const groups = data?.main_groups ?? [];
  const hasCanonical = isRestaurant && groups.length > 0;

  return (
    <div style={{ animation: 'fade-in 0.35s ease' }}>
      <PageHeader
        title="Расходы"
        subtitle={
          selectedUnit && selectedPeriod
            ? `${selectedUnit.name} · ${periodLabel(selectedPeriod.year, selectedPeriod.month)}`
            : 'Расходы'
        }
        actions={
          selectedUnit && (
            <ExportMenu
              report="expenses"
              params={{ businessUnitId: selectedUnit.id, periodId: selectedPeriod?.id }}
            />
          )
        }
      />

      {query.isPending && <Spinner />}
      {query.error && (
        <ErrorState
          message={query.error instanceof Error ? query.error.message : 'Ошибка загрузки'}
          onRetry={() => void query.refetch()}
        />
      )}

      {data && !hasCanonical && (data.rows?.length ?? 0) === 0 && (
        <NoFinancialData unitName={selectedUnit?.name} />
      )}

      {data && hasCanonical && (
        <>
          <ExpenseSummary data={data} wide={wide} />
          <MoversCard growth={data.top_growth} decline={data.top_decline} />
          <StructureChart data={data} />
          {selectedUnit && selectedPeriod && (
            <ExtraordinaryExpenses unitId={selectedUnit.id} periodId={selectedPeriod.id} />
          )}
        </>
      )}
    </div>
  );
}

/** Sums, shares and MoM by canonical group. A share is shown only where its
 * denominator exists — a missing revenue never becomes a fake percentage. */
function ExpenseSummary({ data, wide }: { data: ExpensesResponse; wide: boolean }) {
  const total = data.all_expenses;
  const groups = data.main_groups;

  if (!wide) {
    return (
      <Card style={{ marginBottom: 16 }}>
        <SectionTitle
          right={
            <span style={{ fontFamily: font.mono, fontWeight: 700 }}>
              {formatAmd(toNumber(total?.amount))}
            </span>
          }
        >
          Все расходы
        </SectionTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {groups.map((group) => (
            <div key={group.code} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13 }}>
              <span style={{ color: v('ink-sub'), overflowWrap: 'anywhere' }}>{group.name}</span>
              {/* The figures never shrink below their own width — on a
                  320 px phone the global `min-width: 0` let «17 817 270 ֏»
                  run into its MoM. The caption wraps instead. */}
              <span style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexShrink: 0 }}>
                <span style={{ fontFamily: font.mono, fontWeight: 600 }}>
                  {formatAmd(toNumber(group.amount))}
                </span>
                <MomBadge value={toNumber(group.mom_pct)} />
              </span>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card style={{ marginBottom: 16 }}>
      <SectionTitle
        right={
          <span style={{ fontFamily: font.mono, fontWeight: 700 }}>
            {formatAmd(toNumber(total?.amount))}
          </span>
        }
      >
        Основные группы расходов
      </SectionTitle>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {['Группа', 'Сумма', '% расходов', '% выручки', 'MoM'].map((header, index) => (
              <th
                key={header}
                style={{
                  textAlign: index === 0 ? 'left' : 'right',
                  padding: '6px 8px',
                  fontSize: 11.5,
                  color: v('ink-muted'),
                  fontWeight: 600,
                  borderBottom: `1px solid ${v('line')}`,
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <GroupRow key={group.code} group={group} />
          ))}
          {total && <GroupRow group={total} total />}
        </tbody>
      </table>
    </Card>
  );
}

function GroupRow({ group, total = false }: { group: ExpenseGroupRow; total?: boolean }) {
  const mom = toNumber(group.mom_pct);
  const weight = total ? 700 : 400;
  return (
    <tr>
      <td style={{ ...cell, fontWeight: weight, color: total ? v('ink') : undefined }}>
        {group.name}
      </td>
      <td style={{ ...cell, textAlign: 'right', fontFamily: font.mono, fontWeight: total ? 700 : 600 }}>
        {formatAmd(toNumber(group.amount))}
      </td>
      <td style={{ ...cell, textAlign: 'right', fontFamily: font.mono, color: v('ink-muted') }}>
        {formatPercent(toNumber(group.share_of_expenses), 0)}
      </td>
      <td style={{ ...cell, textAlign: 'right', fontFamily: font.mono, color: v('ink-muted') }}>
        {formatPercent(toNumber(group.share_of_revenue))}
      </td>
      <td
        style={{
          ...cell,
          textAlign: 'right',
          fontFamily: font.mono,
          fontWeight: 600,
          color: mom === null ? v('ink-muted') : mom <= 0 ? v('positive') : v('negative'),
        }}
      >
        {mom === null ? NO_DATA : formatChange(mom)}
      </td>
    </tr>
  );
}

const cell = {
  padding: '8px 8px',
  borderBottom: `1px solid ${v('line-soft')}`,
} as const;

function MomBadge({ value }: { value: number | null }) {
  return (
    <span
      style={{
        fontFamily: font.mono,
        fontWeight: 600,
        fontSize: 12,
        minWidth: 58,
        textAlign: 'right',
        color: value === null ? v('ink-muted') : value <= 0 ? v('positive') : v('negative'),
      }}
    >
      {value === null ? NO_DATA : formatChange(value)}
    </span>
  );
}

/**
 * Real movers by leaf category — not a repeat of the table above.
 *
 * A line with no previous figure is absent by construction: the backend
 * only reports a mover when both months are known, so nothing here has to
 * invent a "+100%" or an ∞ against a missing base.
 */
function MoversCard({ growth, decline }: { growth: ExpenseDriverRow[]; decline: ExpenseDriverRow[] }) {
  return (
    <Card style={{ marginBottom: 16 }}>
      <SectionTitle>Что изменилось сильнее всего</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: 20 }}>
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: v('negative'), marginBottom: 6 }}>
            ТОП-3 РОСТА
          </div>
          <MoverList rows={growth} empty="Нет роста к прошлому месяцу." />
        </div>
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: v('positive'), marginBottom: 6 }}>
            ТОП-3 СНИЖЕНИЯ
          </div>
          <MoverList rows={decline} empty="Нет снижений к прошлому месяцу." />
        </div>
      </div>
    </Card>
  );
}

export function MoverList({ rows, empty }: { rows: ExpenseDriverRow[]; empty: string }) {
  if (rows.length === 0) {
    return <div style={{ fontSize: 12.5, color: v('ink-muted'), padding: '4px 0' }}>{empty}</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((row) => {
        const delta = toNumber(row.delta);
        const current = toNumber(row.current);
        const previous = toNumber(row.previous);
        // Only computed where the base is a real, non-zero number; otherwise
        // the percentage is simply not shown.
        const pct =
          delta !== null && previous !== null && previous !== 0 ? delta / Math.abs(previous) : null;
        return (
          // One row on a desk; on a phone the caption takes its own line and
          // the three figures sit in a fixed grid underneath, so a long
          // category name can wrap without ever moving the numbers.
          <div key={row.category_id} className="mover-row">
            <span className="mover-name">{row.name}</span>
            <div className="mover-figures">
              <span className="mover-current">{formatAmd(current)}</span>
              <span
                className="mover-delta"
                style={{ color: delta !== null && delta > 0 ? v('negative') : v('positive') }}
              >
                {delta === null ? NO_DATA : `${delta > 0 ? '+' : ''}${formatAmd(delta)}`}
              </span>
              <span className="mover-pct">
                {pct === null ? NO_DATA : formatChange(pct)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Jan → the selected month, stacked by the five sections that reconcile to
 * «Все расходы». */
function StructureChart({ data }: { data: ExpensesResponse }) {
  const points = data.monthly_series;
  if (points.length === 0) return null;

  const byCode = new Map(data.main_groups.map((group) => [group.code, group.name]));
  const present = STACKED_GROUP_CODES.filter((code) =>
    points.some((point) => point.groups[code] != null),
  );
  if (present.length === 0) return null;

  const labels = points.map((point) => periodShortLabel(point.month));
  const series = present.map((code, index) => ({
    key: byCode.get(code) ?? code,
    color: GROUP_COLORS[index % GROUP_COLORS.length],
    values: points.map((point) => toNumber(point.groups[code] ?? null)),
  }));

  return (
    <ChartCard title="Структура расходов по месяцам" height={280}>
      <ExpenseStackChart
        data={buildPoints(labels, Object.fromEntries(series.map((s) => [s.key, s.values])))}
        series={series.map(({ key, color }) => ({ key, color }))}
      />
    </ChartCard>
  );
}
